import React, { useCallback, useEffect, useState } from 'react';
import { X, Mail } from 'lucide-react';
export function openContactModal() {
  window.dispatchEvent(new CustomEvent('open-contact-modal'));
}
export function ContactModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
    // Reset form after animation finishes
    setTimeout(() => {
      setIsSuccess(false);
      setFormData({
        name: '',
        email: '',
        organisation: '',
        subject: 'General Inquiry',
        message: ''
      });
    }, 300);
  }, []);
  useEffect(() => {
    window.addEventListener('open-contact-modal', handleOpen);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('open-contact-modal', handleOpen);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOpen, handleClose, isOpen]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Auto close after success
      setTimeout(() => {
        handleClose();
      }, 2500);
    }, 1500);
  };
  if (!isOpen && !isSubmitting && !isSuccess) return null;
  return <div className={`fixed inset-0 z-[9998] flex items-center justify-center p-4 sm:p-6 transition-all duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-16">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-brand-charcoal/50 backdrop-blur-sm transition-opacity" onClick={handleClose} data-id="element-17" />

      {/* Modal Card */}
      <div className={`relative w-full max-w-lg bg-brand-cream rounded-2xl shadow-xl overflow-hidden transition-all duration-300 transform ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`} onClick={e => e.stopPropagation()} data-id="element-18">
        {/* Close Button */}
        <button onClick={handleClose} className="absolute top-4 right-4 p-2 text-brand-brown hover:text-brand-red transition-colors rounded-full hover:bg-brand-shape/20 z-10" aria-label="Close modal" data-id="element-19">
          <X className="w-5 h-5" data-id="element-20" />
        </button>

        <div className="p-8 sm:p-10 max-h-[90vh] overflow-y-auto" data-id="element-21">
          <h3 className="text-2xl font-serif text-brand-charcoal mb-8" data-id="element-22">
            Send us a message
          </h3>

          {isSuccess ? <div className="bg-brand-card/40 border border-brand-gold/30 rounded-xl p-8 text-center py-12" data-id="element-23">
              <div className="w-16 h-16 bg-brand-red/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-24">
                <Mail className="w-8 h-8 text-brand-red" data-id="element-25" />
              </div>
              <h4 className="text-xl font-serif text-brand-charcoal mb-2" data-id="element-26">
                Message sent successfully
              </h4>
              <p className="text-brand-brown leading-relaxed" data-id="element-27">
                Thank you for reaching out. A member of the Firetree team will
                get back to you shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-28">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-29">
                <div data-id="element-30">
                  <label htmlFor="name" className="block text-sm font-medium text-brand-charcoal mb-1.5" data-id="element-31">
                    Full Name *
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-white/50 border border-brand-shape/50 rounded-lg px-4 py-2.5 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all" placeholder="Jane Doe" data-id="element-32" />
                </div>
                <div data-id="element-33">
                  <label htmlFor="email" className="block text-sm font-medium text-brand-charcoal mb-1.5" data-id="element-34">
                    Email Address *
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-white/50 border border-brand-shape/50 rounded-lg px-4 py-2.5 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all" placeholder="jane@example.com" data-id="element-35" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-36">
                <div data-id="element-37">
                  <label htmlFor="organisation" className="block text-sm font-medium text-brand-charcoal mb-1.5" data-id="element-38">
                    Organisation{' '}
                    <span className="text-brand-brown/60 font-normal" data-id="element-39">
                      (Optional)
                    </span>
                  </label>
                  <input type="text" id="organisation" name="organisation" value={formData.organisation} onChange={handleChange} className="w-full bg-white/50 border border-brand-shape/50 rounded-lg px-4 py-2.5 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all" placeholder="Foundation Name" data-id="element-40" />
                </div>
                <div data-id="element-41">
                  <label htmlFor="subject" className="block text-sm font-medium text-brand-charcoal mb-1.5" data-id="element-42">
                    Subject *
                  </label>
                  <select id="subject" name="subject" required value={formData.subject} onChange={handleChange} className="w-full bg-white/50 border border-brand-shape/50 rounded-lg px-4 py-2.5 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all appearance-none" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B6560'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                backgroundSize: '1.2em 1.2em'
              }} data-id="element-43">
                    <option value="General Inquiry" data-id="element-44">General Inquiry</option>
                    <option value="Partnership Interest" data-id="element-45">
                      Partnership Interest
                    </option>
                    <option value="Fund Information" data-id="element-46">Fund Information</option>
                    <option value="Other" data-id="element-47">Other</option>
                  </select>
                </div>
              </div>

              <div data-id="element-48">
                <label htmlFor="message" className="block text-sm font-medium text-brand-charcoal mb-1.5" data-id="element-49">
                  Message *
                </label>
                <textarea id="message" name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full bg-white/50 border border-brand-shape/50 rounded-lg px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all resize-none" placeholder="How can we help you?" data-id="element-50"></textarea>
              </div>

              <div className="pt-2" data-id="element-51">
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center px-8 py-3.5 bg-brand-red text-white font-semibold rounded-full hover:bg-brand-charcoal transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-52">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>}
        </div>
      </div>
    </div>;
}